import FrameShell from './FrameShell';

/* The LinksOS reporting page, recreated faithfully from the live portal
   with a healthy month of internally consistent sample numbers:
   4,120 of 6,448 seats booked (63.9%), 17 no-shows (0.4%), 4,058 rounds
   at a $38 realized rate = $154,204, plus F&B and retail = $163,090. */

const TILES = [
  ['RevPATT', '$96', 'Revenue per available tee time, the golf RevPAR', 'gold'],
  ['Utilization / Fill rate', '63.9%', '4,120 of 6,448 seats booked', ''],
  ['No-show rate', '0.4%', '17 no-shows of 4,120 booked', ''],
  ['Total revenue', '$163,090', 'Rounds $154,204 · F&B $6,486 · Retail $2,400', ''],
  ['Rounds played', '4,058', 'Over the last 30 days', ''],
  ['Avg rate realized', '$38', 'Weighted by booked seats', ''],
  ['MGL platform fee (transparent)', '$2,225', 'What MGL took, no hidden cuts', 'hot'],
];

const REV = [4.2, 3.9, 4.1, 5.8, 6.9, 7.2, 5.1, 4.0, 3.8, 4.2, 5.6, 6.8, 7.1, 5.0, 4.1, 4.0, 4.3, 5.9, 7.0, 7.3, 5.2, 3.9, 4.1, 4.4, 5.7, 6.7, 7.2, 5.3, 4.2, 4.5];
const RP = [78, 72, 76, 108, 128, 134, 95, 74, 70, 78, 104, 126, 132, 93, 76, 74, 80, 110, 130, 136, 97, 72, 76, 82, 106, 124, 134, 99, 78, 84];

const lineFor = (values, max) =>
  values.map((v, i) => `${(6 + (i * 288) / (values.length - 1)).toFixed(1)},${(102 - (v / max) * 92).toFixed(1)}`).join(' ');

const REV_LINE = lineFor(REV, 8);
const FNB_LINE = lineFor(REV.map((v) => v * 0.042), 8);
const RETAIL_LINE = lineFor(REV.map((v) => v * 0.016), 8);

export default function ReportingFrame({ maxWidth }) {
  return (
    <FrameShell caption="LinksOS · reporting · sample data" maxWidth={maxWidth}>
      <div style={{ padding: '16px 18px 18px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          <span style={{ fontSize: '1.15rem', fontWeight: 750, letterSpacing: '-0.02em' }}>Reporting</span>
          <span style={{ fontSize: 9.5, color: 'var(--faint)', marginRight: 'auto' }}>
            Golf-grade business intelligence
          </span>
          {['Last 7 days', 'Last 30 days', 'Last 90 days'].map((r) => (
            <span
              key={r}
              className="mono"
              style={{
                fontSize: 8.5, fontWeight: 600, borderRadius: 10, padding: '3px 9px',
                background: r === 'Last 30 days' ? 'var(--pine)' : 'transparent',
                color: r === 'Last 30 days' ? 'var(--cream)' : 'var(--mut)',
                border: r === 'Last 30 days' ? 'none' : '1px solid var(--line-soft)',
              }}
            >
              {r}
            </span>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 10, alignItems: 'center', border: '1.5px solid var(--gold)', background: 'rgba(244,199,86,0.08)', borderRadius: 9, padding: '8px 12px', marginBottom: 12 }}>
          <span style={{ fontSize: 12 }} aria-hidden="true">💡</span>
          <span>
            <span className="mono" style={{ display: 'block', fontSize: 7.5, letterSpacing: '0.14em', color: 'var(--mut)' }}>WHAT TO DO TODAY</span>
            <span style={{ fontSize: 10, fontWeight: 700 }}>Open seats to fill.</span>{' '}
            <span style={{ fontSize: 9.5, color: 'var(--mut)' }}>Fill is at 64% for Friday. A twilight promo or a Flash Deal can move the open seats.</span>
          </span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(118px, 1fr))', gap: 8, marginBottom: 12 }}>
          {TILES.map(([label, big, sub, tone]) => (
            <div
              key={label}
              style={{
                background: 'var(--card)', borderRadius: 9, padding: '9px 11px',
                border: tone === 'hot' || tone === 'gold' ? '1.5px solid var(--gold)' : '1px solid var(--line-soft)',
              }}
            >
              <div style={{ fontSize: 8, fontWeight: 650, color: 'var(--mut)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
              <div className="mono" style={{ fontSize: '1rem', fontWeight: 600, margin: '2px 0 1px', color: tone === 'gold' ? 'var(--gold)' : 'var(--ink)' }}>{big}</div>
              <div style={{ fontSize: 8, color: 'var(--faint)', lineHeight: 1.35 }}>{sub}</div>
            </div>
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 10 }}>
          <div style={{ background: 'var(--card)', border: '1px solid var(--line-soft)', borderRadius: 9, padding: '10px 12px' }}>
            <div style={{ fontSize: 9.5, fontWeight: 700 }}>Daily revenue by stream</div>
            <div style={{ fontSize: 8, color: 'var(--faint)', marginBottom: 6 }}>Rounds, food and beverage, and retail over the range.</div>
            <svg viewBox="0 0 300 112" style={{ width: '100%', display: 'block' }} aria-hidden="true" focusable="false">
              {[10, 56, 102].map((y) => (
                <line key={y} x1="6" x2="294" y1={y} y2={y} stroke="rgba(31,46,38,0.1)" strokeWidth="0.6" strokeDasharray="2 3" />
              ))}
              <text x="0" y="12" fontFamily="'Spline Sans Mono', monospace" fontSize="6" fill="rgba(31,46,38,0.45)">$8k</text>
              <text x="0" y="58" fontFamily="'Spline Sans Mono', monospace" fontSize="6" fill="rgba(31,46,38,0.45)">$4k</text>
              <text x="0" y="104" fontFamily="'Spline Sans Mono', monospace" fontSize="6" fill="rgba(31,46,38,0.45)">$0</text>
              <polyline points={REV_LINE} fill="none" stroke="#1c3a2a" strokeWidth="1.8" strokeLinejoin="round" />
              <polyline points={FNB_LINE} fill="none" stroke="#f4c756" strokeWidth="1.2" strokeLinejoin="round" />
              <polyline points={RETAIL_LINE} fill="none" stroke="#2f6b4c" strokeWidth="1" strokeLinejoin="round" opacity="0.7" />
            </svg>
            <div style={{ display: 'flex', gap: 12, paddingTop: 4 }}>
              {[['Rounds', '#1c3a2a'], ['F&B', '#f4c756'], ['Retail', '#2f6b4c']].map(([l, c]) => (
                <span key={l} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 8, color: 'var(--mut)' }}>
                  <span style={{ width: 8, height: 3, borderRadius: 2, background: c, display: 'inline-block' }} />
                  {l}
                </span>
              ))}
            </div>
          </div>

          <div style={{ background: 'var(--card)', border: '1px solid var(--line-soft)', borderRadius: 9, padding: '10px 12px' }}>
            <div style={{ fontSize: 9.5, fontWeight: 700 }}>RevPATT by day</div>
            <div style={{ fontSize: 8, color: 'var(--faint)', marginBottom: 6 }}>Revenue per available tee time, your yield curve.</div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 96 }}>
              {RP.map((v, i) => (
                <div key={i} style={{ flex: 1, height: `${(v / 140) * 100}%`, background: 'var(--gold)', borderRadius: '2px 2px 0 0' }} />
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 4 }}>
              {['Jul 10', 'Jul 20', 'Jul 30', 'Aug 7'].map((d) => (
                <span key={d} className="mono" style={{ fontSize: 6.5, color: 'var(--faint)' }}>{d}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </FrameShell>
  );
}
